import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';

const SIDEWALK_CLEANING_FAQ = [
  {
    question: 'How often should commercial sidewalks be cleaned?',
    answer: 'We recommend cleaning commercial sidewalks at least twice a year, or more frequently in high-traffic areas, to maintain safety and curb appeal.'
  },
  {
    question: 'Can you remove gum, stains, and oil from sidewalks?',
    answer: 'Yes! Our professional equipment and cleaning solutions are designed to remove gum, stains, oil, and other tough contaminants from concrete sidewalks.'
  },
  {
    question: 'Is your cleaning process safe for all types of sidewalk surfaces?',
    answer: {
      intro: 'Absolutely. We tailor our cleaning methods for different sidewalk materials, including:',
      list: [
        'Concrete',
        'Pavers',
        'Brick',
        'Stone'
      ],
      outro: 'Our technicians use the right pressure and solutions to ensure a thorough clean without damaging your surfaces.'
    }
  }
];

const SidewalkCleaning = () => {
  const serviceSEO = {
    title: 'Sidewalk Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional sidewalk cleaning in Phoenix, AZ. Remove gum, stains & oil. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Sidewalk Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Sidewalk Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Sidewalk Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional sidewalk cleaning in Phoenix, AZ. Remove gum, stains & oil. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="concrete sidewalk cleaning, commercial sidewalk cleaning, Phoenix sidewalk maintenance, sidewalk gum removal, sidewalk stain removal, walkway cleaning, sidewalk safety cleaning, sidewalk restoration, sidewalk surface cleaning, public access cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Sidewalk Cleaning Banner">
        <picture>
          <source srcSet="/sidewalk_hero.avif" type="image/avif" />
          <source srcSet="/sidewalk_hero.webp" type="image/webp" />
        <img 
          src="/sidewalk_hero.jpeg" 
          alt="Professional sidewalk cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Sidewalk Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Sidewalk Cleaning Services</h2>
        <p className="mb-6 text-gray-700">We provide expert sidewalk cleaning for commercial properties, shopping centers, and public spaces. Our team uses advanced equipment and eco-friendly solutions to keep your walkways safe and looking their best.</p>
        <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">Why Choose Us?</h3>
        <ul className="list-disc pl-6 mb-6 text-gray-700">
          <li>Experienced, professional technicians</li>
          <li>Eco-friendly cleaning solutions</li>
          <li>Removal of gum, stains, and oil</li>
          <li>Flexible scheduling for businesses</li>
          <li>100% satisfaction guarantee</li>
        </ul>
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Sidewalk Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our sidewalk cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={SIDEWALK_CLEANING_FAQ} title="Frequently Asked Questions About Sidewalk Cleaning" />
    </div>
  );
};

export default SidewalkCleaning; 